import { Bar } from "react-chartjs-2"
import { Chart, registerables, TooltipItem, TooltipModel } from "chart.js"
import dayjs from "dayjs"
import styled from "styled-components"

import { ApiTypes, UITypes } from "types"

import getLegendMarginPlugin from "utils/chartjs/getLegendMarginPlugin"
import { defaultChartTooltipOptions } from "utils/chartUtils"
import DateRangeSelect from "components/DateRangeSelect"

import { dateOptions } from "./getWarrantyDevicesDateRangeParams"

Chart.register(...registerables)

const Container = styled.div`
  padding: 20px 35px;
  display: flex;
  flex-direction: column;

  > :first-child {
    align-self: start;
  }
`

const BarChartContainer = styled.div`
  padding-top: 10px;
  height: 400px;
`

const settings = {
  last14Days: {
    labelX: "Days",
    barWidth: 0.9,
  },
  last30Days: {
    labelX: "Days",
    barWidth: 0.7,
  },
  last90Days: {
    labelX: "Days",
    barWidth: 0.7,
  },
}

interface WarrantyDevicesHistoryModalContentProps {
  selectedTime: UITypes.Option[]
  setSelectedTime: (time: UITypes.Option[]) => void
  daysCount: number
  historicalData: ApiTypes.Model.WarrantyDevicesCount[] | null
}

export const WarrantyDevicesHistoryModalContent = ({
  selectedTime,
  daysCount,
  historicalData,
  setSelectedTime,
}: WarrantyDevicesHistoryModalContentProps) => {
  const selectedSettings = settings[selectedTime[0].value as keyof typeof settings]
  const ticks = daysCount

  const data = {
    labels: historicalData?.map(item => item.date) || [],
    datasets: [
      {
        label: "Warranty Devices",
        backgroundColor: "#22c55e",
        data: historicalData?.map(item => item.count) || [],
        barPercentage: selectedSettings.barWidth,
        categoryPercentage: selectedSettings.barWidth,
      },
    ],
  }

  const options = {
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: true,
        align: "start" as const,
        labels: {
          color: "#94A3B8",
          usePointStyle: true,
          pointStyle: "rect",
        },
      },
      tooltip: {
        ...defaultChartTooltipOptions,
        displayColors: false,
        titleAlign: "center" as const,
        callbacks: {
          title: function (this: TooltipModel<"bar">, tooltipItems: TooltipItem<"bar">[]) {
            return `Devices: ${tooltipItems[0].formattedValue}`
          },
          label: function (this: TooltipModel<"bar">, tooltipItem: TooltipItem<"bar">) {
            return dayjs(tooltipItem.label).format("DD MMMM, YYYY")
          },
        },
      },
    },
    responsive: true,
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: "Days",
          color: "#94A3B8",
        },
        ticks: {
          color: "#94A3B8",
          callback: (value: number | string) => Number(value) + 1,
          maxTicksLimit: ticks + 1,
        },
      },
      y: {
        suggestedMax: 5,
        title: {
          display: true,
          text: "Devices",
          color: "#94A3B8",
        },
        ticks: {
          maxTicksLimit: 6,
          color: "#94A3B8",
        },
      },
    },
  }

  return (
    <Container>
      <DateRangeSelect options={dateOptions} selectedTime={selectedTime} setSelectedTime={setSelectedTime} />
      <BarChartContainer>
        <Bar data={data} options={options} plugins={[getLegendMarginPlugin(10)]} />
      </BarChartContainer>
    </Container>
  )
}
